import type { DateRange } from "@daypicker/react"
import { BOOKING_DRAFT_KEY, BOOKING_DRAFT_TTL_MS, clearBookingSessionStorage } from "./bookingSession"
import { PRODUCT_CATALOG, productSlugFromSearch } from "./catalogue"
import { localDate } from "./format"
import type { BookingDraft, BookingDraftPayload } from "./types"

export function readBookingDraft(): BookingDraft | null {
    if (typeof window === "undefined") return null
    try {
        const stored = JSON.parse(window.sessionStorage.getItem(BOOKING_DRAFT_KEY) || "null") as (Partial<BookingDraft> & Pick<BookingDraft, "version" | "updatedAt" | "productSlug" | "quantity">) | null
        const value = stored ? {
            ...stored,
            bookingPane: stored.bookingPane === "addons" ? "addons" : "selection",
            selectedAddonIds: Array.isArray(stored.selectedAddonIds)
                ? stored.selectedAddonIds.filter((id): id is string => typeof id === "string")
                : [],
        } as BookingDraft : null
        const age = value ? Date.now() - value.updatedAt : 0
        if (!value || value.version !== 1 || !Number.isFinite(value.updatedAt) || age < 0 || age >= BOOKING_DRAFT_TTL_MS) {
            if (value) clearBookingSessionStorage()
            return null
        }
        if (!PRODUCT_CATALOG.some((item) => item.slug === value.productSlug) || !Number.isInteger(value.quantity) || value.quantity < 1) {
            clearBookingSessionStorage()
            return null
        }
        if (PRODUCT_CATALOG.find((item) => item.slug === value.productSlug)?.type === "dehumidifier") {
            value.bookingPane = "selection"
            value.selectedAddonIds = []
        }
        const requestedSlug = productSlugFromSearch(window.location.search)
        if (requestedSlug && requestedSlug !== value.productSlug) {
            clearBookingSessionStorage()
            return null
        }
        // Older drafts may contain controls that are no longer offered at checkout.
        const hadOptionalDetails = Boolean(value.customer.companyName?.trim() || value.customer.deliveryInstructions?.trim())
        value.customer = { ...value.customer, companyName: "", deliveryInstructions: "" }
        if (hadOptionalDetails && value.completedStep === "details") value.completedStep = "booking"
        return value
    } catch {
        clearBookingSessionStorage()
        return null
    }
}

export function rangeFromDraft(draft: BookingDraft | null): DateRange | undefined {
    if (!draft?.deliveryDate) return undefined
    return {
        from: localDate(draft.deliveryDate),
        to: draft.collectionDate ? localDate(draft.collectionDate) : undefined,
    }
}

export function draftHasCompleteDetails(draft: BookingDraft): boolean {
    const hasAddress = draft.manualDeliveryEntry
        ? draft.manualDeliveryAddress.line1.trim().length >= 3 && draft.manualDeliveryAddress.city.trim().length >= 2
        : Boolean(draft.selectedDeliveryAddress)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.customer.email.trim())
        && /^[+0-9()\s-]{7,30}$/.test(draft.customer.phone.trim())
        && hasAddress
        && (!draft.differentBilling || Boolean(draft.billingComplete && draft.billingAddress))
}

export function draftPayload(draft: BookingDraft): BookingDraftPayload {
    const { updatedAt: _updatedAt, ...payload } = draft
    return payload
}
